import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import type { LibraryFolder } from "@/pages/library_v2/components/filesView/types";
import { useTrans } from "@/translations";

import { useCallback, useContext, useEffect, useRef, useState } from "react";

type Props = {
  folder: LibraryFolder;
  setIsRenaming: (value: boolean) => void;
};

export const FolderRenameView: React.FC<Props> = ({
  folder,
  setIsRenaming,
}) => {
  const { t } = useTrans();
  const { setGlobalAlert } = useGlobalAlert();
  const { currentTerminal } = useTerminalsState();
  const { refetchFolders } = useContext(LibraryPageContext);

  const [renameValue, setRenameValue] = useState(folder.name);
  const renameContainerRef = useRef<HTMLDivElement>(null);

  const { mutate: renameFolder } = trpc.library.updateFolderName.useMutation({
    onSuccess: () => {
      refetchFolders();
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentDeleteFailed",
      });
      console.error("Error deleting document:", error);
    },
  });

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        renameContainerRef.current &&
        !renameContainerRef.current.contains(e.target as Node)
      ) {
        setIsRenaming(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [setIsRenaming]);

  const onRenameFolder = useCallback(
    async (currentFolderName: string, newFolderName: string) => {
      await renameFolder({
        terminalId: currentTerminal?.id!,
        currentFolderName,
        newFolderName,
      });
    },
    [currentTerminal?.id, renameFolder],
  );

  const onSaveRename = useCallback(
    (folderName: string) => {
      const trimmed = renameValue.trim();
      if (!trimmed) return;
      setIsRenaming(false);
      onRenameFolder(folderName, trimmed);
    },
    [renameValue, setIsRenaming, onRenameFolder],
  );

  return (
    <div ref={renameContainerRef} className="flex gap-[5px] py-[3px]">
      <input
        autoFocus
        value={renameValue}
        onChange={(e) => setRenameValue(e.target.value)}
        placeholder={t("libraryPage:folderListRenamePlaceholder")}
        className="min-w-0 flex-1 rounded border border-black px-2 py-[6px] text-[12.5px] outline-none"
      />
      <button
        type="button"
        onClick={() => onSaveRename(folder.name)}
        className="cursor-pointer rounded bg-black px-[9px] py-[6px] text-[11.5px] font-semibold text-white"
      >
        {t("actions:save")}
      </button>
    </div>
  );
};
